<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useLocalStorage, useStepper } from '@vueuse/core';
import axios from 'axios';

import { useOtcAPIService } from '@/composables/services/useOtcAPIService';
import { useBankTransfer } from '@/composables/useBankTransfer';
import { type ClientEECode, useErrorModal } from '@/composables/useErrorModal';
import { type OtcData, useOtc } from '@/composables/useOtc';
import { useSettings } from '@/composables/useSettings';
import { OTC_NETWORK_TIP_KEY } from '@/composables/useTips';
import { isNaN, isZero, lessThan } from '@/helpers/bigmath';
import { formatAmount, formatTokenAmount } from '@/helpers/formatters';
import { fade, type MotionVariants, slideLeft, slideRight } from '@/helpers/motion';
import { sameToken } from '@/helpers/tokens';
import { addSentryBreadcrumb, captureSentryException } from '@/logs/sentry';
import {
  type BankTransferRecipient,
  BankTransferRecipientType
} from '@/references/axios/bankTransfer/types';
import { AmountMode, type Fees } from '@/references/axios/transactions/types';
import { FORM_SET_AMOUNT_DEBOUNCE, NATIVE_OPTIONS, SECOND } from '@/references/constants';
import { CurrencyCode } from '@/references/currency';
import { ExpectedError } from '@/references/ExpectedError';
import type { Network } from '@/references/network';
import type { Token, TokenWithEurPrice } from '@/references/tokens';
import { RouteNames } from '@/router';

import BeneficiaryInfo from '@/components/BeneficiaryInfo.vue';
import { InnerLayoutButtonType } from '@/components/layouts/InnerLayout.types';
import InnerLayout from '@/components/layouts/InnerLayout.vue';
import type { PrepareFormAmountOption } from '@/components/PrepareForm.types';
import PrepareForm from '@/components/PrepareForm.vue';
import TokenSelector from '@/components/TokenSelector.vue';
import TransactionReviewOtc from '@/components/TransactionReviewOtc.vue';
import type { InputTokenOrFiatMode } from '@/components/ui/UiTokenInput.types';

enum OtcViewStep {
  Token = 'token',
  Form = 'form',
  Review = 'review'
}

interface Props {
  id: string;
}

const props = defineProps<Props>();

const availableNetworks = useOtcAPIService().getAvailableNetworks();
const persistedNetwork = useLocalStorage<Network>('lastSelectedNetwork', availableNetworks[0]);

const state = reactive<{
  isMounted: boolean;
  previousStep: OtcViewStep | null;
  token: TokenWithEurPrice | null;
  network: Network;
  isLoading: boolean;
  isSoonReloadSwapData: boolean;
  amount: string;
  outputData: OtcData | null;
  inputMode: InputTokenOrFiatMode;
  reloadCounter: number;
  error: string;
  fees: Fees | null;
}>({
  isMounted: false,
  previousStep: null,
  token: null,
  network: getInitialNetwork(),
  isLoading: false,
  isSoonReloadSwapData: false,
  amount: '',
  outputData: null,
  inputMode: 'TOKEN',
  reloadCounter: 0,
  error: '',
  fees: null
});

const prepareFormComponent = ref<typeof PrepareForm>();

let debounceAmountTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
let reloadSwapDataInterval: ReturnType<typeof setTimeout> | undefined = undefined;
let calculateSecondAmountAbortController = new AbortController();

const toast = useErrorModal();
const stepper = useStepper([OtcViewStep.Token, OtcViewStep.Form, OtcViewStep.Review]);
const { t } = useI18n();
const { replace } = useRouter();
const { getToEurAmount, getFees } = useOtc();
const { minOtcAmountEur } = useSettings();
const { recipients } = useBankTransfer();

const beneficiaryId = computed((): number => {
  const id = Number(props.id);

  if (isNaN(id)) {
    return -1;
  }

  return id;
});

const networks = computed((): Network[] => {
  return useOtcAPIService().getAvailableNetworks();
});

const internalMotion = computed((): MotionVariants<never> | undefined => {
  if (!state.isMounted) {
    return fade;
  }

  if (!state.previousStep || stepper.isAfter(state.previousStep)) {
    return slideRight;
  }

  return slideLeft;
});

const alternativeAmountText = computed((): string => {
  if (state.token === null) {
    return '0';
  }

  if (state.amount === '' || state.outputData === null) {
    if (state.inputMode === 'FIAT') {
      return formatTokenAmount(
        0,
        Math.min(state.token.decimals, 6),
        state.token.symbol,
        AmountMode.CodeLast
      );
    }

    return formatAmount(0, 2, CurrencyCode.EUR, AmountMode.CodeLast);
  }

  if (state.inputMode === 'FIAT') {
    return formatTokenAmount(
      state.outputData.toAmount,
      Math.min(state.token.decimals, 6),
      state.token.symbol,
      AmountMode.CodeLast
    );
  }

  return formatAmount(state.outputData.toAmount, 2, CurrencyCode.EUR, AmountMode.CodeLast);
});

const isLoadingToAmount = computed((): boolean => {
  if (isZero(state.amount)) {
    return false;
  }

  if (state.isSoonReloadSwapData) {
    return true;
  }

  return state.amount !== '' && state.outputData === null;
});

const options = computed((): PrepareFormAmountOption[] => {
  let items: PrepareFormAmountOption[] = [];

  if (state.token === null) {
    return items;
  }

  items = NATIVE_OPTIONS.map<PrepareFormAmountOption>((item) => {
    const value = String(item);
    return {
      label: formatTokenAmount(item, 0, state.token?.symbol ?? '', AmountMode.CodeLast),
      value: value,
      disabled: false,
      handler() {
        setAmount(value);
      }
    };
  });

  return items;
});

const tipTitle = computed((): string => {
  switch (stepper.current.value) {
    case OtcViewStep.Token:
      return t('otc.token.modal.title');
    case OtcViewStep.Form:
      return t('otc.form.modal.title');
    case OtcViewStep.Review:
      return t('otc.review.modal.title');
    default:
      return '';
  }
});

const tipDescription = computed((): string => {
  switch (stepper.current.value) {
    case OtcViewStep.Token:
      return t('otc.token.modal.description');
    case OtcViewStep.Form:
      return t('otc.form.modal.description');
    case OtcViewStep.Review:
      return t('otc.review.modal.description');
    default:
      return '';
  }
});

const beneficiaryData = computed((): BankTransferRecipient | undefined =>
  recipients.value.find((i) => i.id === beneficiaryId.value)
);

const beneficiaryName = computed((): string => {
  if (beneficiaryData.value === undefined) {
    return '';
  }
  if (beneficiaryData.value.type === BankTransferRecipientType.Business) {
    return beneficiaryData.value.firstName.toUpperCase();
  }

  return `${beneficiaryData.value.firstName} ${beneficiaryData.value.lastName}`.toUpperCase();
});

const beneficiaryShortName = computed((): string => {
  if (beneficiaryData.value === undefined) {
    return '';
  }
  if (beneficiaryData.value.nickname) {
    return beneficiaryData.value.nickname;
  }

  return beneficiaryName.value;
});

const title = computed((): string => {
  if (stepper.current.value === OtcViewStep.Review) {
    return '';
  }

  return t('otc.otc');
});

function getInitialNetwork(): Network {
  if (availableNetworks.includes(persistedNetwork.value)) {
    return persistedNetwork.value;
  }

  persistedNetwork.value = availableNetworks[0];
  return persistedNetwork.value;
}

function goBack(): void {
  if (stepper.current.value === OtcViewStep.Token) {
    replace({ name: RouteNames.Home });
    return;
  }

  state.previousStep = stepper.current.value;
  stepper.goToPrevious();
}

function goNext(): void {
  state.previousStep = stepper.current.value;
  stepper.goToNext();
}

async function loadOtcData(token: Token, tokenAmount: string, signal: AbortSignal): Promise<void> {
  try {
    addSentryBreadcrumb({
      level: 'info',
      message: 'loading otc data'
    });
    state.outputData = await getToEurAmount(token, tokenAmount, signal);
  } catch (error) {
    if (error === 'aborted') {
      return;
    }

    addSentryBreadcrumb({
      level: 'error',
      message: 'fail to get swap data',
      data: {
        error: error,
        forData: {
          token,
          tokenAmount
        }
      }
    });
    captureSentryException(error);
  }
}

function selectToken(token: TokenWithEurPrice): void {
  state.token = token;
  goNext();
}

function setAmount(amount: string): void {
  state.error = '';

  clearTimeout(debounceAmountTimeout);

  if (state.token === null) {
    return;
  }

  state.amount = amount;
  state.outputData = null;

  if (isZero(amount) || amount === '') {
    return;
  }

  const tokenFrom = state.token;
  const tokenAmount = state.amount;

  calculateSecondAmountAbortController.abort();
  calculateSecondAmountAbortController = new AbortController();

  debounceAmountTimeout = setTimeout(() => {
    loadOtcData(tokenFrom, tokenAmount, calculateSecondAmountAbortController.signal);
  }, FORM_SET_AMOUNT_DEBOUNCE);

  clearInterval(reloadSwapDataInterval);

  reloadSwapDataInterval = setInterval(() => {
    if (stepper.current.value === OtcViewStep.Review) {
      return;
    }
    switch (state.reloadCounter) {
      case 13: {
        state.isSoonReloadSwapData = true;
        state.reloadCounter = state.reloadCounter + 1;
        return;
      }
      case 15: {
        state.reloadCounter = 0;
        loadOtcData(tokenFrom, tokenAmount, calculateSecondAmountAbortController.signal).finally(
          () => {
            state.isSoonReloadSwapData = false;
          }
        );
        break;
      }
      default: {
        state.reloadCounter = state.reloadCounter + 1;
      }
    }
  }, SECOND);
}

function validate(validEmptyString = true): string {
  if (state.amount === '' || isZero(state.amount) || state.amount.length === 0) {
    if (!validEmptyString) {
      return t('forms.minimumWarn', {
        min: formatAmount(minOtcAmountEur.value, 2, CurrencyCode.EUR, AmountMode.SymbolFirst)
      });
    }
  }

  if (state.outputData == null) {
    if (validEmptyString) {
      return '';
    }

    return t('forms.minimumWarn', {
      min: formatAmount(minOtcAmountEur.value, 2, CurrencyCode.EUR, AmountMode.SymbolFirst)
    });
  }

  if (lessThan(state.outputData.toAmount, minOtcAmountEur.value)) {
    return t('forms.minimumWarn', {
      min: formatAmount(minOtcAmountEur.value, 2, CurrencyCode.EUR, AmountMode.SymbolFirst)
    });
  }

  return '';
}

async function continueClick(): Promise<void> {
  if (!state.amount) {
    prepareFormComponent.value?.shake();
    prepareFormComponent.value?.focus();
    return;
  }

  state.error = validate(false);

  if (state.error || isZero(state.amount)) {
    prepareFormComponent.value?.shake();
    prepareFormComponent.value?.focus();
    return;
  }

  if (state.token === null) {
    return;
  }

  if (state.isLoading) {
    return;
  }

  state.isLoading = true;
  let otcData = state.outputData;

  const ac = new AbortController();

  //maybe prev. calculating falled or stale
  if (otcData === null) {
    calculateSecondAmountAbortController.abort();
    clearTimeout(debounceAmountTimeout);
    clearTimeout(reloadSwapDataInterval);
    try {
      const amount = state.amount;

      otcData = await getToEurAmount(state.token, amount, ac.signal);
      state.outputData = otcData;
      state.error = validate();
      if (state.error !== undefined) {
        state.isLoading = false;
        prepareFormComponent.value?.shake();
        prepareFormComponent.value?.focus();
        return;
      }
    } catch (error) {
      state.isLoading = false;

      if (axios.isCancel(error)) {
        return;
      }

      if (error instanceof ExpectedError) {
        toast.auto(error);
        return;
      }

      toast.auto(
        new ExpectedError<ClientEECode>('otcDataFetchFailed', {
          cause: new Error('Failed to get EUR amount by token', { cause: error }),
          sentryHandle: true
        })
      );
      return;
    }
  }

  try {
    state.fees = await getFees(beneficiaryId.value, ac.signal);
  } catch (error) {
    state.isLoading = false;

    if (axios.isCancel(error)) {
      return;
    }

    if (error instanceof ExpectedError) {
      toast.auto(error);
      return;
    }

    toast.auto(
      new ExpectedError<ClientEECode>('otcDataFetchFailed', {
        cause: new Error('Failed to load fees', { cause: error }),
        sentryHandle: true
      })
    );
    return;
  }

  // TODO.
  goNext();
  state.isLoading = false;
}

function clearBeneficiary(): void {
  replace({ name: RouteNames.Home });
}

watch(
  () => state.token,
  (nv, pv) => {
    if (nv === null) {
      clearTimeout(debounceAmountTimeout);
      clearTimeout(reloadSwapDataInterval);
      return;
    }

    if (nv && pv && sameToken(nv, pv)) {
      return;
    }

    setAmount('');
  }
);

onMounted(() => {
  state.isMounted = true;
  nextTick(() => {
    if (beneficiaryData.value === undefined) {
      replace({ name: RouteNames.Home });
    }
  });
});

onBeforeUnmount(() => {
  clearTimeout(debounceAmountTimeout);
  clearTimeout(reloadSwapDataInterval);
});
</script>

<template>
  <section class="otc-view">
    <InnerLayout
      :title="title"
      :button-type="InnerLayoutButtonType.Back"
      :tip-title="tipTitle"
      :tip-description="tipDescription"
      @back="goBack"
    >
      <BeneficiaryInfo
        v-if="
          stepper.current.value === OtcViewStep.Token || stepper.current.value === OtcViewStep.Form
        "
        v-motion="internalMotion"
        class="otc-view__header"
        :name="beneficiaryShortName"
        @clear="clearBeneficiary"
      />
      <TokenSelector
        v-if="stepper.current.value === OtcViewStep.Token"
        v-model:network="state.network"
        v-motion="internalMotion"
        :networks="networks"
        :local-storage-tip-key="OTC_NETWORK_TIP_KEY"
        :sticky="false"
        @select="selectToken"
      />
      <PrepareForm
        v-if="stepper.current.value === OtcViewStep.Form && state.token"
        ref="prepareFormComponent"
        v-motion="internalMotion"
        :token="state.token"
        :amount="state.amount"
        :error="state.error"
        :options="options"
        :second-amount-text="alternativeAmountText"
        :input-mode="state.inputMode"
        :is-skip-max-fiat-amount="true"
        :is-second-amount-pulsing="isLoadingToAmount"
        available-text=" "
        :has-toggle-button="false"
        :is-toggle-button-clickable="false"
        :is-submit-button-disabled="isLoadingToAmount"
        @update:amount="setAmount"
        @submit="continueClick"
      />
      <TransactionReviewOtc
        v-if="
          stepper.current.value === OtcViewStep.Review &&
          state.outputData &&
          beneficiaryData &&
          state.fees
        "
        v-motion="internalMotion"
        :otc-data="state.outputData"
        :fees="state.fees"
        :bank-transfer-data="beneficiaryData"
      />
    </InnerLayout>
  </section>
</template>

<style scoped>
.otc-view {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.otc-view__header {
  position: sticky;
  top: 96px;
  z-index: var(--z-index-header);
  margin: 0 0 40px;
}

@media (max-width: 919px) {
  .otc-view__header {
    top: 80px;
    margin: 0 0 24px;
  }
}
</style>
